import React, { useEffect, useMemo, useState } from "react";
import { Card, Table, Pagination, Form, InputGroup, Button, Badge, Alert, Spinner } from "react-bootstrap";
import { IconSearch, IconChevronUp, IconChevronDown, IconReceipt2, IconDownload, IconAlertCircle } from "@tabler/icons-react";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { Helmet } from "react-helmet-async";

const STATUS_COLORS = {
  paid: "success",
  success: "success",
  completed: "success",
  pending: "warning",
  processing: "warning",
  failed: "danger",
  canceled: "danger",
  cancelled: "danger",
  refunded: "secondary",
};

function formatMoney(value, currency) {
  const num = typeof value === "number" ? value : parseFloat(String(value || "0"));
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: currency || "USD", maximumFractionDigits: 2 }).format(num);
  } catch {
    return `${num.toFixed(2)} ${currency || ""}`.trim();
  }
}

function toCSV(rows) {
  const headers = ["Reference", "Date", "Status", "Product", "Price", "Currency", "Credits", "InvoiceURL"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const body = rows.map(o =>
    [o.order_reference, o.created_at, o.status, o.product, o.price, o.currency, o.credits, o.invoice_url].map(esc).join(",")
  );
  return [headers.join(","), ...body].join("\n");
}

const MyOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [downloadingRef, setDownloadingRef] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError("");
      try {
        const response = await fetchWithAuth("/api/my-orders");
        const data = await response.json();
        setOrders(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Error loading orders:", e);
        setError("Failed to load orders. Please try again later.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filteredSorted = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list = !q
      ? orders
      : orders.filter((o) =>
          [o.order_reference, o.product, o.status, o.currency]
            .map((v) => String(v || "").toLowerCase())
            .some((s) => s.includes(q))
        );

    list = list.slice().sort((a, b) => {
      const dir = sortOrder === "asc" ? 1 : -1;
      const av = a[sortBy];
      const bv = b[sortBy];

      if (sortBy === "created_at") {
        const ad = new Date(av).getTime();
        const bd = new Date(bv).getTime();
        return (ad - bd) * dir;
      }
      if (sortBy === "price" || sortBy === "credits") {
        const an = parseFloat(av ?? 0);
        const bn = parseFloat(bv ?? 0);
        return (an - bn) * dir;
      }
      const as = String(av ?? "").toLowerCase();
      const bs = String(bv ?? "").toLowerCase();
      if (as < bs) return -1 * dir;
      if (as > bs) return 1 * dir;
      return 0;
    });

    return list;
  }, [orders, searchQuery, sortBy, sortOrder]);

  const total = filteredSorted.length;
  const totalPages = Math.ceil(total / limit) || 1;
  const pageClamped = Math.min(page, totalPages);
  const startIdx = total === 0 ? 0 : (pageClamped - 1) * limit + 1;
  const endIdx = total === 0 ? 0 : Math.min(pageClamped * limit, total);
  const paginated = total === 0 ? [] : filteredSorted.slice((pageClamped - 1) * limit, pageClamped * limit);

  const getPaginationPages = (current, totalPages) => {
    if (totalPages < 1) return [];
    const delta = 2;
    const pages = new Set([1, totalPages]);
    for (let i = current - delta; i <= current + delta; i++) {
      if (i > 1 && i < totalPages) pages.add(i);
    }
    const sorted = Array.from(pages).sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
      result.push(sorted[i]);
      if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1) result.push("...");
    }
    return result;
  };
  const pagesToShow = getPaginationPages(pageClamped, totalPages);
  const showPagination = total > 0 && pagesToShow.length > 0;

  const renderSortHeader = (label, key, className = "") => (
    <th
      onClick={() => {
        if (sortBy === key) {
          setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
          setSortBy(key);
          setSortOrder("asc");
        }
        setPage(1);
      }}
      style={{ cursor: "pointer", whiteSpace: "nowrap" }}
      className={className}
    >
      {label}{" "}
      {sortBy === key &&
        (sortOrder === "asc" ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />)}
    </th>
  );

  const exportCSV = () => {
    const csv = toCSV(filteredSorted);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "my-orders.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // --- filename from Content-Disposition ---
  const pickFilename = (res, fallback) => {
    const cd = res.headers?.get?.("content-disposition") || "";
    const m = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(cd);
    if (m) return decodeURIComponent(m[1] || m[2] || fallback);
    return fallback;
  };

  // === NEW: download via fetchWithAuth (blob) so Authorization header is included ===
  const handleDownload = async (reference, price) => {
    const total = Number(price || 0);
    if (!reference || !isFinite(total) || total <= 0) return;

    setDownloadingRef(reference);
    try {
      const res = await fetchWithAuth(`/api/my-orders/${encodeURIComponent(reference)}/invoice`, {
        method: "GET",
      });
      if (!res.ok) {
        console.error("Invoice download error:", res.status);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const filename = pickFilename(res, `invoice_${reference}.pdf`);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Invoice download error:", e);
    } finally {
      setDownloadingRef(null);
    }
  };

  return (
    <>
      <Helmet><title>My Orders - IndexBooster</title></Helmet>

      <Card className="mt-4">
        <div className="card-stamp">
          <div className="card-stamp-icon bg-primary">
            <IconReceipt2 size={18} />
          </div>
        </div>

        <Card.Header className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
          <div className="d-flex align-items-center gap-2">
            <h3 className="m-0">My Orders</h3>
          </div>
          <div className="d-flex align-items-center gap-2">
            <InputGroup style={{ maxWidth: 280 }}>
              <InputGroup.Text><IconSearch size={18} /></InputGroup.Text>
              <Form.Control
                placeholder="Search orders…"
                value={searchQuery}
                onChange={(e) => { setPage(1); setSearchQuery(e.target.value); }}
              />
            </InputGroup>
            <Button variant="btn btn-success" onClick={exportCSV} title="Export CSV">
              <IconDownload size={18} className="me-1" /> Export
            </Button>
          </div>
        </Card.Header>

        {error && (
          <div className="px-3 pt-2">
            <Alert variant="danger" className="mb-0">
              <IconAlertCircle size={16} className="me-2" />
              {error}
            </Alert>
          </div>
        )}

        <div className="table-responsive">
          <Table className="table card-table table-vcenter text-nowrap datatable" hover responsive>
            <thead>
              <tr>
                {renderSortHeader("Reference", "order_reference")}
                {renderSortHeader("Date", "created_at", "text-center")}
                {renderSortHeader("Status", "status", "text-center")}
                {renderSortHeader("Product", "product", "text-center")}
                {renderSortHeader("Price", "price", "text-center")}
                {renderSortHeader("Credits", "credits", "text-center")}
                <th className="text-center">Invoice</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-5">
                    <Spinner animation="border" role="status" className="me-2" />
                    <span className="text-muted">Loading orders…</span>
                  </td>
                </tr>
              ) : paginated.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-5 text-secondary">
                    No orders found
                  </td>
                </tr>
              ) : (
                paginated.map((order, index) => {
                  const raw = String(order.status || "").toLowerCase();
                  const isPaid = raw === "paid" || raw === "success" || raw === "completed" || raw === "pending_link";
                  const displayStatus = isPaid ? "PAID" : (order.status || "PENDING");
                  const badgeColor = isPaid ? "success" : (STATUS_COLORS[raw] || "secondary");
                  const total = Number(order.price || 0);
                  const showDash = !isFinite(total) || total <= 0;

                  return (
                    <tr key={index}>
                      <td style={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis" }} title={order.order_reference}>
                        {order.order_reference}
                      </td>
                      <td className="text-center">
                        <div>{new Date(order.created_at).toLocaleDateString()}</div>
                        <div className="text-secondary small">{new Date(order.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</div>
                      </td>
                      <td className="text-center">
                        <Badge bg={`${badgeColor}-lt`} text={badgeColor} className="text-uppercase">
                          {displayStatus}
                        </Badge>
                      </td>
                      <td className="text-center">{order.product}</td>
                      <td className="text-center">{formatMoney(order.price, order.currency)}</td>
                      <td className="text-center">{order.credits}</td>
                      <td className="text-center">
                        {showDash ? (
                          <span className="text-muted">—</span>
                        ) : (
                          <Button
                            size="md"
                            onClick={() => handleDownload(order.order_reference, order.price, order.status)}
                            disabled={downloadingRef === order.order_reference}
                          >
                            <IconDownload size={16} className="me-1" />
                            {downloadingRef === order.order_reference ? "Preparing…" : "Download"}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>

        <Card.Footer className="d-flex flex-wrap justify-content-between align-items-center px-4">
          <div className="d-flex align-items-center gap-3 mb-2 mb-md-0">
            {showPagination && (
              <Pagination className="mb-0">
                <Pagination.Prev disabled={pageClamped === 1} onClick={() => setPage(pageClamped - 1)}>«</Pagination.Prev>
                {pagesToShow.map((p, i) =>
                  p === "..." ? (
                    <Pagination.Ellipsis key={`ellipsis-${i}`} disabled />
                  ) : (
                    <Pagination.Item key={`page-${p}`} active={p === pageClamped} onClick={() => setPage(p)}>
                      {p}
                    </Pagination.Item>
                  )
                )}
                <Pagination.Next disabled={pageClamped === totalPages} onClick={() => setPage(pageClamped + 1)}>»</Pagination.Next>
              </Pagination>
            )}
            <div className="text-muted">
              Showing <b>{startIdx}</b>–<b>{endIdx}</b> of <b>{total}</b> entries
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted">Show</span>
            <Form.Select
              value={limit}
              onChange={(e) => { setPage(1); setLimit(parseInt(e.target.value)); }}
              size="sm"
            >
              {[25, 50, 100, 200].map((val) => (
                <option key={val} value={val}>{val}</option>
              ))}
            </Form.Select>
            <span className="text-muted">entries</span>
          </div>
        </Card.Footer>
      </Card>
    </>
  );
};

export default MyOrders;
